import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    stripeExpressAccountLinkResponseSchema,
    stripeExpressBrandingSchema,
    stripeExpressConnectionParamsSchema,
    stripeExpressCreateBodySchema,
    stripeExpressRefreshCacheResponseSchema,
    stripeExpressRemintBodySchema,
    stripeExpressStartOverBodySchema,
    stripeExpressStartOverResponseSchema,
    stripeExpressUpdateBrandingBodySchema,
} from "@common/schemas/domains/stripe/stripe-express.schema";

const c = initContract();

// Kick-created Express connections for native invoice payment collection. Every
// endpoint is authenticated and refuses View-only writes. The Account Link
// hosted URL is minted on demand and never persisted (Stripe expires it in
// ~5 minutes). The `refresh_url` route (a plain 302 redirect) lives on the
// controller, not here, because ts-rest cannot model a redirect response.
export const stripeExpressContract = c.router(
    {
        // Create the entity's Express account on first use, or remint (and
        // un-archive) the existing one — never a second Express per entity.
        createOrResume: {
            method: "POST",
            path: "/",
            body: stripeExpressCreateBodySchema,
            responses: {
                200: stripeExpressAccountLinkResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        // Finish setup: remint an Account Link for an existing Express connection.
        remintAccountLink: {
            method: "POST",
            path: "/:connectionId/account-link",
            pathParams: stripeExpressConnectionParamsSchema,
            body: stripeExpressRemintBodySchema,
            responses: {
                200: stripeExpressAccountLinkResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        // Return-page toast source: live retrieve + write cache, echo the flags.
        refreshCache: {
            method: "POST",
            path: "/:connectionId/refresh-cache",
            pathParams: stripeExpressConnectionParamsSchema,
            body: z.object({}).optional(),
            responses: {
                200: stripeExpressRefreshCacheResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        startOver: {
            method: "POST",
            path: "/:connectionId/start-over",
            pathParams: stripeExpressConnectionParamsSchema,
            body: stripeExpressStartOverBodySchema,
            responses: {
                200: stripeExpressStartOverResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        // Express Dashboard login link. Minted on click, single-use, never stored.
        createLoginLink: {
            method: "POST",
            path: "/:connectionId/login-link",
            pathParams: stripeExpressConnectionParamsSchema,
            body: z.object({}).optional(),
            responses: {
                200: stripeExpressAccountLinkResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        // Live-reads the account's branding (logo, brand/accent colors) straight
        // from Stripe — never cached, since it can also be edited directly on
        // Stripe's own Dashboard.
        getBranding: {
            method: "GET",
            path: "/:connectionId/branding",
            pathParams: stripeExpressConnectionParamsSchema,
            responses: {
                200: stripeExpressBrandingSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        // Saves branding directly to Stripe via `accounts.update`; Kick persists
        // none of it. Omitted fields are left unchanged, `null` clears them.
        updateBranding: {
            method: "PATCH",
            path: "/:connectionId/branding",
            pathParams: stripeExpressConnectionParamsSchema,
            body: stripeExpressUpdateBrandingBodySchema,
            responses: {
                200: stripeExpressBrandingSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/stripe-express",
    },
);
